import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { applyThemeStylesheet } from '@/services/theme-service';

const THEME_STORAGE_KEY = 'OrnaThemeColor';

const themeMap: Record<string, string> = {
  'kendo.customGrey': 'theme-gray-dark',
  'kendo.custom': 'theme-blue-dark',
};

function getThemeFromStorage(): string {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  return (stored && themeMap[stored]) || '';
}

interface ThemeContextValue {
  theme: string;
  setTheme: (theme: string) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: '',
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState(getThemeFromStorage);

  const setTheme = useCallback((newTheme: string) => {
    setThemeState(newTheme);
  }, []);

  // Listen for theme changes from Vue 2 code and other tabs
  useEffect(() => {
    const handler = () => {
      setThemeState(getThemeFromStorage());
    };
    const storageHandler = (e: StorageEvent) => {
      if (e.key !== THEME_STORAGE_KEY) return;
      // The stylesheet swap happens in the originating tab only — mirror it here
      applyThemeStylesheet(localStorage.getItem(THEME_STORAGE_KEY) ?? '');
      handler();
    };
    window.addEventListener('vue2-theme-change', handler);
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('vue2-theme-change', handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
